import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { Feedback } from '../../types'
import './FeedbackView.css'

export default function FeedbackView() {
  const [feedback, setFeedback] = useState<Feedback[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'bug' | 'enhancement'>('all')

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const { data, error } = await supabase
          .from('feedback')
          .select('*')
          .order('created_at', { ascending: false })

        if (error) throw error
        setFeedback(data || [])
      } catch (err) {
        console.error('Error fetching feedback:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchFeedback()

    // Subscribe to changes
    const subscription = supabase
      .channel('feedback_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'feedback' }, (payload) => {
        setFeedback((prev) => [payload.new as Feedback, ...prev.filter((f) => f.id !== (payload.new as Feedback).id)])
      })
      .subscribe()

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const filtered = feedback.filter((f) => filter === 'all' || f.type === filter)
  const bugCount = feedback.filter((f) => f.type === 'bug').length
  const enhancementCount = feedback.filter((f) => f.type === 'enhancement').length

  return (
    <div className="view feedback-view">
      <div className="view-header">
        <h1>Feedback & Bugs</h1>
        <p>User feedback, bugs & enhancement requests</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">{feedback.length}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{bugCount}</div>
          <div className="stat-label">Bugs</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{enhancementCount}</div>
          <div className="stat-label">Enhancements</div>
        </div>
      </div>

      <div className="card">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filter === 'bug' ? 'active' : ''}`}
            onClick={() => setFilter('bug')}
          >
            🐛 Bugs
          </button>
          <button
            className={`filter-btn ${filter === 'enhancement' ? 'active' : ''}`}
            onClick={() => setFilter('enhancement')}
          >
            ✨ Enhancements
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading feedback...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💬</div>
            <p>No feedback yet</p>
          </div>
        ) : (
          <div className="feedback-list">
            {filtered.map((item) => (
              <div key={item.id} className="feedback-item">
                <div className="feedback-header">
                  <span className="feedback-type">
                    {item.type === 'bug' ? '🐛' : '✨'} {item.type}
                  </span>
                  <span className="feedback-date">
                    {new Date(item.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="feedback-subject">{item.subject}</div>
                <div className="feedback-details">{item.details}</div>
                {item.email && <div className="feedback-email">📧 {item.email}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
